import { useState, type FormEvent } from "react";
import { useLocation } from "react-router";
import { ApiError } from "@repo/api-client";
import { useAuth } from "@repo/auth";
import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { rememberReturnPath } from "../../shell/returnPath";
import { useVenue } from "../../shell/VenueContext";
import { managerPinApi, managerPinKeys } from "./managerPinApi";

const PIN_SHAPE = /^\d{6}$/;

/** Map a rejected save to its message (ADR-0008); the server decides, this only words it. */
function errorKey(err: unknown): string {
  const code =
    err instanceof ApiError
      ? (err.problem as { errorCode?: string } | undefined)?.errorCode
      : undefined;
  switch (code) {
    case "RECENT_SIGN_IN_REQUIRED":
      return "settings.pin.error.signIn";
    case "CURRENT_PIN_INVALID":
      return "settings.pin.error.currentInvalid";
    case "MANAGER_PIN_UNAVAILABLE":
      return "settings.pin.error.unavailable";
    case "PIN_LOCKED":
      return "settings.pin.error.locked";
    case "VALIDATION_FAILED":
      return "settings.pin.error.format";
    default:
      return "settings.pin.error.generic";
  }
}

/**
 * `/v/:venueId/settings/pin` — the caller's own manager PIN at this venue. Creating or resetting one needs
 * a recent sign-in, so with a stale one this screen first offers a re-authentication, which comes back here
 * through [rememberReturnPath]. Changing an existing PIN can use the current one instead. The server
 * enforces all of it; this screen only shows the path that will be accepted.
 */
export function ManagerPinSettings() {
  const { venueId } = useVenue();
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: managerPinKeys.status(venueId),
    queryFn: () => managerPinApi.get(venueId),
  });

  function confirmIdentity() {
    rememberReturnPath(location.pathname);
    login({ prompt: "login", loginHint: user?.email });
  }

  if (status.isLoading) {
    return (
      <p className={css({ color: "fg.muted", fontSize: "sm" })}>{t("settings.pin.loading")}</p>
    );
  }
  if (status.isError || !status.data) {
    return (
      <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
        {t("settings.pin.loadError")}
      </p>
    );
  }

  const configured = status.data.configured === true;
  const recentSignIn = status.data.recentSignIn === true;

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "5", maxW: "md" })}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        <h2 className={css({ fontSize: "lg", fontWeight: "semibold", color: "fg.default" })}>
          {t("settings.pin.title")}
        </h2>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("settings.pin.intro")}</p>
        <p className={css({ fontSize: "sm", color: "fg.default" })} data-testid="pin-status">
          {t(configured ? "settings.pin.statusSet" : "settings.pin.statusUnset")}
        </p>
      </div>

      {!configured && !recentSignIn ? (
        <ConfirmIdentity onConfirm={confirmIdentity} />
      ) : (
        <PinForm
          venueId={venueId}
          needsCurrentPin={configured && !recentSignIn}
          onForgot={confirmIdentity}
          onChanged={() => qc.invalidateQueries({ queryKey: managerPinKeys.status(venueId) })}
        />
      )}
    </section>
  );
}

function ConfirmIdentity({ onConfirm }: { onConfirm: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "3",
        alignItems: "flex-start",
      })}
    >
      <h3 className={css({ fontSize: "sm", fontWeight: "semibold", color: "fg.default" })}>
        {t("settings.pin.confirm.title")}
      </h3>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("settings.pin.confirm.body")}</p>
      <Button size="sm" onClick={onConfirm}>
        {t("settings.pin.confirm.action")}
      </Button>
    </div>
  );
}

function PinForm({
  venueId,
  needsCurrentPin,
  onForgot,
  onChanged,
}: {
  venueId: string;
  needsCurrentPin: boolean;
  onForgot: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [pin, setPin] = useState("");
  const [repeat, setRepeat] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    if (needsCurrentPin && current === "")
      return setErrorMessageKey("settings.pin.error.currentRequired");
    if (!PIN_SHAPE.test(pin)) return setErrorMessageKey("settings.pin.error.format");
    if (pin !== repeat) return setErrorMessageKey("settings.pin.error.mismatch");

    setErrorMessageKey(null);
    setSaving(true);
    try {
      await managerPinApi.set(venueId, needsCurrentPin ? { pin, currentPin: current } : { pin });
      setCurrent("");
      setPin("");
      setRepeat("");
      setSaved(true);
      await onChanged();
    } catch (err) {
      setErrorMessageKey(errorKey(err));
      // The sign-in aged out while they typed: reload so the screen offers the way forward.
      if (errorKey(err) === "settings.pin.error.signIn") await onChanged();
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className={css({ display: "flex", flexDirection: "column", gap: "3" })}
    >
      {errorMessageKey ? (
        <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
          {t(errorMessageKey)}
        </p>
      ) : null}
      {needsCurrentPin ? (
        <PinField
          label={t("settings.pin.current")}
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
        />
      ) : null}
      <PinField
        label={t("settings.pin.new")}
        value={pin}
        onChange={setPin}
        autoComplete="new-password"
      />
      <PinField
        label={t("settings.pin.repeat")}
        value={repeat}
        onChange={setRepeat}
        autoComplete="new-password"
      />
      <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
        <Button size="sm" type="submit" loading={saving}>
          {t("settings.pin.save")}
        </Button>
        {saved ? (
          <output className={css({ fontSize: "sm", color: "fg.muted" })}>
            {t("settings.pin.saved")}
          </output>
        ) : null}
      </div>
      {needsCurrentPin ? (
        <button
          type="button"
          onClick={onForgot}
          className={css({
            alignSelf: "flex-start",
            fontSize: "sm",
            color: "colorPalette.text",
            textDecoration: "underline",
            cursor: "pointer",
          })}
        >
          {t("settings.pin.forgot")}
        </button>
      ) : null}
    </form>
  );
}

function PinField({
  label,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
}) {
  return (
    <label
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "1",
        fontSize: "sm",
        maxW: "xs",
      })}
    >
      <span className={css({ fontWeight: "medium", color: "fg.default" })}>{label}</span>
      <input
        type="password"
        inputMode="numeric"
        maxLength={6}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={css({
          px: "3",
          py: "2",
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "md",
        })}
      />
    </label>
  );
}
